import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles, Bot } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import apiClient from '../../lib/apiClient';
import { ChatComponentRenderer } from './components';
import { dispatchComponentAction } from './components/dispatchComponentAction';
import { friendlyToolLabel } from './components/toolLabels';
import { useCanvas } from '../../contexts/CanvasContext';
import './ChatWidget.css';

const SUGGESTIONS = [
  // Procurement
  'What is the current classification coverage?',
  'Show me unclassified items by spend',
  // Sales AI
  'Run NIST CSF gap analysis for Cortex XSIAM and NGFW',
  'What products deliver threat prevention?',
  'Compare Cortex XDR with CrowdStrike',
  'Search documentation for SSL decryption',
];

const STORAGE_KEY = 'ts_chat_api_key';

function ChatWidget({ selectedDepartment = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(null);
  const [byokKey, setByokKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [tokenUsage, setTokenUsage] = useState({ total: 0 });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  // Conversation session id — groups this chat's turns into one
  // ai_conversations row on the backend (UUID required by the column)
  const sessionIdRef = useRef(crypto.randomUUID());

  // Canvas integration
  const { addWidgetsFromAI } = useCanvas();

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      // Build history for context (exclude tool metadata)
      const history = [...messages, userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      const body = {
        message: text.trim(),
        history: history.slice(-20),
        sessionId: sessionIdRef.current,
      };

      // Working context: the department selected in the app, so the AI
      // defaults its tools/suggestions to the account being worked
      if (selectedDepartment) {
        body.context = {
          department: {
            department_id: selectedDepartment.department_id,
            acronym: selectedDepartment.acronym,
            official_name: selectedDepartment.official_name,
          },
        };
      }

      // Include BYOK key if user has one
      if (byokKey) {
        body.apiKey = byokKey;
      }

      const data = await apiClient.post('/api/chat', body, { timeoutMs: 90000 });

      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        tools: data.tools_used,
        usage: data.usage,
        ui_components: data.ui_components,
        suggested_actions: data.suggested_actions,
      };

      setMessages(prev => [...prev, assistantMessage]);
      setAccessDenied(null);

      // Add UI components to canvas panel
      if (data.ui_components && data.ui_components.length > 0) {
        addWidgetsFromAI(data.ui_components, text.trim());
      }

      // Track cumulative token usage
      if (data.usage) {
        setTokenUsage(prev => ({
          total: prev.total + (data.usage.total_tokens || 0),
          usingOwnKey: data.usage.using_own_key,
        }));
      }
    } catch (err) {
      const msg = err.message || 'Failed to send message';
      if (msg.includes('CHAT_ACCESS_DENIED') || msg.includes('403')) {
        setAccessDenied(msg);
      } else if (msg.includes('BYOK_KEY_REQUIRED')) {
        setShowKeyInput(true);
        setError('Your account requires a personal API key. Enter it below.');
      } else if (msg.includes('401') || msg.includes('No authorization')) {
        setError('Not authenticated. Please log in.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [messages, loading, byokKey, addWidgetsFromAI, selectedDepartment]);

  // Listen for queries from the canvas panel
  useEffect(() => {
    const handleCanvasQuery = (e) => {
      const query = e.detail;
      if (query) {
        setIsOpen(true);
        // Delay slightly to ensure panel is open
        setTimeout(() => {
          sendMessage(query);
        }, 100);
      }
    };

    window.addEventListener('canvas:query', handleCanvasQuery);
    return () => window.removeEventListener('canvas:query', handleCanvasQuery);
  }, [sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleSuggestion = (text) => {
    sendMessage(text);
  };

  // Handle actions from interactive UI components
  const handleComponentAction = useCallback((action) => {
    return dispatchComponentAction(action, {
      sendMessage,
      prefillInput: (value) => {
        setInput(value);
        inputRef.current?.focus();
      },
    });
  }, [sendMessage]);

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button className="cw-fab" onClick={() => setIsOpen(true)} title="Ask AI">
          <MessageCircle size={24} />
          <span className="cw-fab-badge" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="cw-panel">
          {/* Header */}
          <div className="cw-header">
            <div className="cw-header-left">
              <div className="cw-header-icon">
                <Sparkles size={18} color="white" />
              </div>
              <div>
                <div className="cw-header-title">TechStack AI</div>
                <div className="cw-header-subtitle">Procurement analyst assistant</div>
              </div>
            </div>
            {tokenUsage.total > 0 && (
              <span style={{
                fontSize: '0.6rem', color: '#94a3b8', padding: '2px 6px',
                background: 'rgba(255,255,255,0.05)', borderRadius: 4,
              }}>
                {(tokenUsage.total / 1000).toFixed(1)}k tokens
                {tokenUsage.usingOwnKey && ' (your key)'}
              </span>
            )}
            <button className="cw-close-btn" onClick={() => setIsOpen(false)}>
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="cw-messages">
            {/* Access Denied */}
            {accessDenied && messages.length === 0 && (
              <div className="cw-welcome">
                <h3>Chat Not Enabled</h3>
                <p>{accessDenied}</p>
                <p style={{ fontSize: '0.75rem', marginTop: 8 }}>Ask an admin to enable chat for your account, or add your own Anthropic API key.</p>
                {!byokKey && (
                  <button className="cw-suggestion" onClick={() => setShowKeyInput(true)}>
                    Use my own API key
                  </button>
                )}
              </div>
            )}

            {/* BYOK Key Input */}
            {showKeyInput && (
              <div className="cw-welcome">
                <h3>Enter Your Anthropic API Key</h3>
                <p>Your key is stored locally in your browser only.</p>
                <input
                  type="password"
                  placeholder="sk-ant-..."
                  value={byokKey}
                  onChange={e => setByokKey(e.target.value)}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: 8, marginTop: 8,
                    background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                    color: 'var(--color-text)', fontSize: '0.8rem',
                  }}
                />
                <button
                  className="cw-suggestion"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    if (byokKey.startsWith('sk-ant-')) {
                      localStorage.setItem(STORAGE_KEY, byokKey);
                      setShowKeyInput(false);
                      setAccessDenied(null);
                      setError(null);
                      // Also save to backend
                      apiClient.post('/api/chat-access/set-key', { apiKey: byokKey }).catch(() => {});
                    } else {
                      setError('Key must start with sk-ant-');
                    }
                  }}
                >
                  Save Key
                </button>
              </div>
            )}

            {/* Welcome */}
            {messages.length === 0 && !loading && !accessDenied && !showKeyInput && (
              <div className="cw-welcome">
                <h3>How can I help?</h3>
                <p>I can search procurement data, check classification quality, find data gaps, and more.</p>
                <div className="cw-suggestions">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      className="cw-suggestion"
                      onClick={() => handleSuggestion(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i}>
                <div className={`cw-message cw-message-${msg.role}`}>
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown
                      components={{
                        // Ensure tables render properly
                        table: ({ children }) => (
                          <table>{children}</table>
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    msg.content
                  )}
                </div>
                {/* Interactive UI Components */}
                {msg.ui_components && msg.ui_components.length > 0 && (
                  <div className="cw-ui-components">
                    <ChatComponentRenderer
                      components={msg.ui_components}
                      suggestedActions={msg.suggested_actions}
                      onAction={handleComponentAction}
                    />
                  </div>
                )}
                {msg.tools && msg.tools.length > 0 && (
                  <div className="cw-tools">
                    {msg.tools.map((t, j) => (
                      <span key={j} className="cw-tool-tag">
                        {friendlyToolLabel(t.tool)} ({t.duration_ms}ms)
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="cw-typing">
                <Bot size={14} style={{ color: '#94a3b8' }} />
                <div className="cw-typing-dot" />
                <div className="cw-typing-dot" />
                <div className="cw-typing-dot" />
              </div>
            )}

            {error && (
              <div className="cw-error">{error}</div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="cw-input-area">
            <textarea
              ref={inputRef}
              className="cw-input"
              placeholder="Ask about procurement or Palo Alto products..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            <button
              className="cw-send-btn"
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || loading}
            >
              {loading ? <Loader2 size={16} className="cw-spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ChatWidget;
